// What the tabs chrome shows for a given `/chrome-extension/extension/**` path: which product
// name sits in the header, which tab indicator is active, and what that tab is called.
//
// ⚠️ TWO OF THE FOUR FIELDS SURVIVE EXTRACTION AND TWO DO NOT. `routes.json` carries the
// state `data`, so `tabName: 'Searches'` and `hideTabIndicators: true` come straight from it —
// and they are read from there rather than repeated here, because a literal in the route table
// is the generated truth. But `tabGroup: ShortlistGroup.Hunters` and
// `extensionProduct: ExtensionProduct.Candidates` are ENUM MEMBER REFERENCES, which the
// extractor records as `<computed>`; one of them is even a thunk
// (`extensionProduct: () => ExtensionProduct.Candidates`, line 383) and could not be a literal
// however good the extractor got. So those two are transcribed, with their call sites, the
// same way the auth header table is.
//
// From `extension/extension.state.tabs.config.ng1.ts`:
//
//   349   Shortlist                       extensionProduct: ExtensionProduct.Candidates
//   1794  ClientContacts                  extensionProduct: ExtensionProduct.ClientContacts
//   482   HunterEditAlert                 tabGroup: ShortlistGroup.Hunters
//   615   HunterReview                    tabGroup: ShortlistGroup.Hunters
//   1577  HunterExternalSourceReview      tabGroup: ShortlistGroup.Hunters
//   882   CandidateProfileNotFound        tabGroup: ShortlistGroup.Candidate
//   946   CandidateProfile                tabGroup: ShortlistGroup.Candidate
//   1155  CandidateProfileMore            tabGroup: ShortlistGroup.Candidate
//   1923  ClientContactHunterEditAlert    tabGroup: ClientContactGroup.Hunters
//   2010  ClientContactHunterReview       tabGroup: ClientContactGroup.Hunters
//   2241  ClientContactProfileNotFound    tabGroup: ClientContactGroup.ClientContact
//   2305  ClientContactProfile            tabGroup: ClientContactGroup.ClientContact
//   2504  ClientContactProfileMore        tabGroup: ClientContactGroup.ClientContact
//
// ⚠️ RESOLVED BY LONGEST PREFIX, because ui-router `data` is INHERITED. Only the states above
// declare a `tabGroup`; `…/review/candidate/profile` declares none and takes `HunterReview`'s,
// which is why it shows SEARCHES rather than a second tab. Deriving the group from the path
// instead — "contains /candidate" — gets that case exactly backwards, since the path does
// contain it. Same reason `guardsFor` prefix-matches the auth flags.
import type { ExtensionTabGroup } from '@woo-ext/ui'
import { inheritedData, inheritedString } from './routeData.ts'

/** `extensionProductDisplayNames` — shortlist-header.component.ng1.ts:98. */
export const PRODUCT_DISPLAY_NAMES = {
  candidates: 'Candidates',
  clientContacts: 'Contacts',
} as const

export type ExtensionProduct = keyof typeof PRODUCT_DISPLAY_NAMES

/**
 * The two second-level abstract states each declare one product for their whole subtree
 * (lines 349 and 1794), so the path prefix IS the answer rather than a guess at one.
 */
export function productFor(pathname: string): ExtensionProduct | undefined {
  if (pathname.startsWith('/chrome-extension/extension/shortlist')) return 'candidates'
  if (pathname.startsWith('/chrome-extension/extension/client-contact')) return 'clientContacts'
  return undefined
}

/** Path prefix → the `tabGroup` the state at that prefix declares. */
const TAB_GROUPS: ReadonlyArray<readonly [string, ExtensionTabGroup]> = [
  ['/chrome-extension/extension/shortlist/edit-alert', 'hunters'],
  ['/chrome-extension/extension/shortlist/review', 'hunters'],
  ['/chrome-extension/extension/shortlist/external-source-review', 'hunters'],
  ['/chrome-extension/extension/shortlist/profile-not-found', 'candidate'],
  ['/chrome-extension/extension/shortlist/candidate', 'candidate'],
  ['/chrome-extension/extension/client-contact/edit-alert', 'clientContactHunters'],
  ['/chrome-extension/extension/client-contact/review', 'clientContactHunters'],
  ['/chrome-extension/extension/client-contact/profile-not-found', 'clientContact'],
  ['/chrome-extension/extension/client-contact/client-contact', 'clientContact'],
]

function longestPrefix<T>(pathname: string, table: ReadonlyArray<readonly [string, T]>): T | undefined {
  return [...table]
    .filter(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    .sort(([a], [b]) => b.length - a.length)[0]?.[1]
}

export function tabGroupFor(pathname: string): ExtensionTabGroup | undefined {
  return longestPrefix(pathname, TAB_GROUPS)
}

// `tabName` and `hideTabIndicators` are read from `routes.json` rather than transcribed:
// both are literals, so the generator already has them right, and a copy here would be a
// second place for them to drift. Inherited, like every other `data` key — see routeData.ts.
export function tabNameFor(pathname: string): string | undefined {
  return inheritedString(pathname, 'tabName')
}

/** `ng-if="!extCtrl.$state.current.data.hideTabIndicators"` — the whole strip, not one row. */
export function hidesTabIndicators(pathname: string): boolean {
  return inheritedData(pathname, 'hideTabIndicators') === true
}
